import { ReactNode } from 'react'

import { Avatar, Box, Link, Spinner, Text } from '@factorialco/deprecated-design-system'
import {
  GoalsMetricValueStatusEnum,
  PerformanceGoalsMetricDetailsPaneEmployeeFragment,
  PerformanceGoalsMetricDetailsPanelDocument,
  PerformanceGoalsMetricDetailsPanelUpdateHistoryFragment,
} from 'generated/resources/graphql'
import moment from 'moment'

import Sidepanel from 'modules/core/components/Sidepanel'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import { MetricAssessmentStatusTag } from 'modules/goals/components/MetricAssessmentStatusTag'
import { MetricHistoryChart } from 'modules/goals/components/MetricHistoryChart'
import { NormalizedMetric, normalizeMetric } from 'modules/goals/lib/normalizeMetric'

interface Props {
  metricId: string
  onClose: () => void
}

const ValueBox: React.FC<{ label: string; children: ReactNode }> = ({ label, children }) => (
  <Box flexGrow gap="s6" component="article">
    <Box>
      <Text weight="medium" size="200">
        {label}
      </Text>
    </Box>
    <Box>
      <Text size="200">{children}</Text>
    </Box>
  </Box>
)

const Employee: React.FC<{ employee: PerformanceGoalsMetricDetailsPaneEmployeeFragment }> = ({
  employee,
}) => (
  <Box gap="s12" flexDirection="row" alignItems="center">
    <Avatar
      size="s24"
      label={employee?.access?.fullName ?? ''}
      url={buildUrl(employee?.access?.avatar)}
    />
    <Box width="full">{employee?.access?.fullName}</Box>
  </Box>
)

const percentage = (start: number, target: number, current: number) => {
  if (start === target) {
    return current === target ? 100 : 0
  }
  return Math.round(((current - start) / (target - start)) * 100)
}

const UpdateHistory: React.FC<{
  metric: NormalizedMetric<PerformanceGoalsMetricDetailsPanelUpdateHistoryFragment>
}> = ({ metric }) => {
  const translations: Record<GoalsMetricValueStatusEnum, string> = {
    achieved: i18n.t('performance_goals.qualitative_status.achieved'),
    not_achieved: i18n.t('performance_goals.qualitative_status.not_achieved'),
    over_achieved: i18n.t('performance_goals.qualitative_status.over_achieved'),
  }

  return (
    <Box gap="s16">
      {metric.history.map((update) => (
        <Box flexDirection="row" key={update.id} gap="s12" alignItems="center">
          {update.author && (
            <Box flexGrow>
              <Employee employee={update.author} />
            </Box>
          )}
          {update.qualitativeValue && (
            <Box width="s240">{translations[update.qualitativeValue]}</Box>
          )}
          {update.quantitativeValue && (
            <>
              <Box width="s80">
                {percentage(
                  update.quantitativeValue.startValue,
                  update.quantitativeValue.targetValue,
                  update.quantitativeValue.currentValue
                )}
                %
              </Box>
              <Box width="s64" flexNoShrink>
                {i18n.formatNumber(update.quantitativeValue.currentValue)}
              </Box>
            </>
          )}
          <Box width="s96" textAlign="right" flexNoShrink>
            {moment(update.createdAt).format('L')}
          </Box>
        </Box>
      ))}
    </Box>
  )
}

export const MetricDetailsPanel: React.FC<Props> = ({ metricId, onClose }) => {
  const { data, loading } = useQuery(PerformanceGoalsMetricDetailsPanelDocument, {
    variables: { id: metricId },
  })

  const metric = data?.goals?.metric
  const normalizedMetric = metric ? normalizeMetric(metric) : undefined
  const { navigation } = useNavigation()

  return (
    <Sidepanel
      width="wide"
      open
      onClose={onClose}
      title={i18n.t('performance_goals.metric_details_panel.title')}
    >
      {loading && (
        <Box width="full" height="full" justifyContent="center" alignItems="center">
          <Spinner />
        </Box>
      )}
      {normalizedMetric && metric && (
        <Box gap="s24">
          <Box flexDirection="row">
            <Box width="full">
              <Text weight="semibold" size="300">
                {normalizedMetric.title}
              </Text>
            </Box>
            <Box flexNoShrink>
              <MetricAssessmentStatusTag metric={metric} />
            </Box>
          </Box>
          <Box flexDirection="row" gap="s24">
            {metric.assignee && (
              <ValueBox label={i18n.t('performance_goals.metric_details_panel.assigned_to')}>
                <Employee employee={metric.assignee} />
              </ValueBox>
            )}
            {metric.author && (
              <ValueBox label={i18n.t('performance_goals.metric_details_panel.created_by')}>
                <Employee employee={metric.author} />
              </ValueBox>
            )}
          </Box>
          {metric.metricGroup && (
            <Box>
              <ValueBox label={i18n.t('performance_goals.metric_details_panel.goal')}>
                <Link.Router
                  to={navigation.goals.groups.group.fullPath({
                    id: metric.metricGroup.id,
                  })}
                >
                  {metric.metricGroup.title}
                </Link.Router>
              </ValueBox>
            </Box>
          )}
          {normalizedMetric?.type === 'quantitative' && (
            <>
              <Box flexDirection="row">
                <ValueBox label={i18n.t('performance_goals.metric_details_panel.values.initial')}>
                  {i18n.formatNumber(normalizedMetric.quantitativeValue.startValue)}
                </ValueBox>
                <ValueBox label={i18n.t('performance_goals.metric_details_panel.values.target')}>
                  {i18n.formatNumber(normalizedMetric.quantitativeValue.targetValue)}
                </ValueBox>
                <ValueBox label={i18n.t('performance_goals.metric_details_panel.values.current')}>
                  {i18n.formatNumber(normalizedMetric.quantitativeValue.currentValue)} (
                  {normalizedMetric.progressPercentageValue}%)
                </ValueBox>
                <ValueBox label={i18n.t('performance_goals.metric_details_panel.start_date')}>
                  {moment(normalizedMetric.startOn).format('L')}
                </ValueBox>
                <ValueBox label={i18n.t('performance_goals.metric_details_panel.deadline')}>
                  {moment(normalizedMetric.endOn).format('L')}
                </ValueBox>
              </Box>
              <Text.Header3>
                {i18n.t('performance_goals.metric_details_panel.progress_over_time')}
              </Text.Header3>
              <MetricHistoryChart metric={normalizedMetric} />
            </>
          )}
          <Box gap="s16">
            <Text.Header3>
              {i18n.t('performance_goals.metric_details_panel.update_history')}
            </Text.Header3>
            <UpdateHistory metric={normalizedMetric} />
          </Box>
        </Box>
      )}
    </Sidepanel>
  )
}
